import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Check, ChevronRight, ChevronDown, X, Play, BookOpen, Users, Target, Zap, Award, Star, Mail, Clock, Shield, Sparkles } from 'lucide-react';

// SEO Constants
const SEO = {
  title: "AI for Creatives: Build IP That Sells | Ramon Govea",
  description: "Learn the complete AI workflow to take your story from concept to pitch-ready. Taught by Hollywood veteran Ramon Govea (Stan Lee, Scopely, Baron Davis, Lion Forge, $2.6M raised). Course includes 11 modules, templates & community access.",
  ogImage: "https://customer-assets.emergentagent.com/job_6ff09686-3a90-4ae2-8c29-5f23b98b61ac/artifacts/gtmhmjg7_AI%20FOR%20CREATIVES.PNG",
  url: "https://ramongovea.com/ai-tools-for-creatives"
};

// Course Modules Data
const courseModules = [
  { id: 1, title: "Welcome to the Lab", duration: "~7 minutes", description: "Why AI changes everything for solo creators • Ethical framework • Toolkit setup" },
  { id: 2, title: "The Foundation — Your Story Bible", duration: "~15 minutes", description: "What is a story bible • The 5 Pillars framework • AI-powered world building" },
  { id: 3, title: "Character Design with AI", duration: "~18 minutes", description: "From words to faces • Consistency & turnarounds • When to hire humans" },
  { id: 4, title: "World Visualization", duration: "~16 minutes", description: "Building your visual stage • Environment prompts • Mood boards & visual libraries" },
  { id: 5, title: "Storyboarding & Scene Visualization", duration: "~14 minutes", description: "Thinking in sequences • Scene-by-scene workflow • From boards to production" },
  { id: 6, title: "Writing Assistance — Your AI Co-Pilot", duration: "~12 minutes", description: "Breaking writer's block • What AI writes well vs. what needs your voice" },
  { id: 7, title: "Voice & Audio", duration: "~13 minutes", description: "ElevenLabs basics • Creating character voices • Narration & dialogue" },
  { id: 8, title: "Video & Animation", duration: "~15 minutes", description: "HeyGen avatar video • Runway ML effects • Creating trailers & pitch videos" },
  { id: 9, title: "The Pitch Deck", duration: "~14 minutes", description: "What buyers want to see • Structure of winning pitches • Professional integration" },
  { id: 10, title: "Complete Workflow", duration: "~16 minutes", description: "Full creation pipeline • Time management • Live case study walkthrough" },
  { id: 11, title: "Ethics, Ownership & The Future", duration: "~12 minutes", description: "Copyright & AI content • Ethical use • Building hybrid teams (AI + humans)" },
];

// FAQ Data
const faqData = [
  { q: "Do I need to know how to code?", a: "No. This course uses no-code AI tools. If you can copy-paste, you can do this." },
  { q: "Do I need to be able to draw?", a: "Nope. That's the whole point. AI handles the visual generation. You handle the creative direction." },
  { q: "What AI tools will I need?", a: "ChatGPT (or Claude). Free tiers exist for most, but to generate content you want to pitch, you will need a small budget for tokens. Budget estimate: $40-75+/month for paid tiers (optional but recommended)." },
  { q: "Is this just \"how to use ChatGPT\"?", a: "No. This is a complete system for taking your story from concept to pitch-ready, using AI to handle execution while you provide creative vision. It's about process & workflow, not step-by-step tool tutorials (YouTube tutorials are free)." },
  { 
    q: "I'm worried about AI replacing artists. Should I feel guilty?", 
    a: "Short answer: No. Here's why.",
    extended: [
      { type: "heading", text: "The ethical framework (covered throughout the video course):" },
      { type: "bullet", text: "AI for R&D, humans for production. Use AI to figure out what you want—visual development, not final assets. When you can afford to hire artists, you will." },
      { type: "bullet", text: "You're not replacing anyone. You're not hiring a team right now because you CAN'T. AI lets you visualize and develop your concept so you can GET funding and hire that team to execute that vision." },
      { type: "bullet", text: "This is a bridge, not a replacement. Think of AI-generated images like Pinterest mood boards—they show direction, not final product." },
      { type: "heading", text: "What buyers care about:" },
      { type: "text", text: "They want to see your vision executed. Whether you used AI for R&D or hired a team doesn't matter—what matters is whether the final product is worth paying for." },
      { type: "heading", text: "Bottom line:" },
      { type: "text", text: "If you're asking this question, you're the right kind of creator. You care about ethics. The entire course walks you through exactly how to use AI responsibly and within the constraints of existing legal frameworks." }
    ]
  },
  { q: "How long does it take to complete?", a: "The course is ~2.5 hours of video. But the real work is DOING it. Plan on 4-8 weeks to fully build out your IP using the workflows taught." },
  { 
    q: "What if I don't like it?", 
    a: "The \"First Win in 30 Days\" Guarantee:",
    extended: [
      { type: "numbered", items: [
        "Watch Modules 1-4",
        "Follow the workflows",
        "Create at least ONE piece of usable IP content (a character design, an environment sketch, a pitch deck slide—anything)"
      ]},
      { type: "text", text: "If you don't create at least one piece of content you're proud to show someone within 30 days, email us. We'll refund you in full—and let you keep all the templates." },
      { type: "heading", text: "Why we're this confident:" },
      { type: "text", text: "These are step-by-step workflows built from 15+ years of Hollywood development experience. If you follow the process, you WILL get results. If you don't, we don't deserve your money." },
      { type: "text", text: "No questions asked. No hoops. Just email support@ramongovea.com." }
    ]
  },
  { q: "When do I get access?", a: "Modules 1-4 are available immediately after purchase. Modules 5-11 are released weekly (two per week). All templates and the book are available Day 1." },
  { q: "Can I use the IP I create to sell?", a: "Yes. 100%. What you create is yours. This course teaches you HOW to create it, but the IP belongs to you." },
  { q: "What's the difference between the tiers?", a: "Foundation ($147) teaches you to BUILD IP. Pro ($497) teaches you to BUILD + SELL IP (includes sales lessons + pitch review). VIP ($1,997) gives you direct access to Ramon for strategy and pitch coaching." },
];

// FAQ Item Component
const FAQItem = ({ question, answer, extended }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const renderExtendedContent = () => {
    if (!extended) return null;
    
    return extended.map((item, i) => {
      if (item.type === 'heading') {
        return <p key={i} className="text-white font-semibold mt-4 mb-2">{item.text}</p>;
      }
      if (item.type === 'bullet') {
        return (
          <div key={i} className="flex items-start gap-3 ml-2 mb-2">
            <span className="text-[#E5C77A] mt-1">•</span>
            <span className="text-white/70">{item.text}</span>
          </div>
        );
      }
      if (item.type === 'numbered') {
        return (
          <ol key={i} className="list-decimal ml-6 mb-4 space-y-2 text-white/70">
            {item.items.map((text, j) => (
              <li key={j}>{text}</li>
            ))}
          </ol>
        );
      }
      if (item.type === 'text') {
        return <p key={i} className="text-white/70 mb-3">{item.text}</p>;
      }
      return null;
    });
  };
  
  return (
    <div className="border-b border-white/10">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-6 flex items-center justify-between text-left hover:text-[#E5C77A] transition-colors"
      >
        <span className="text-lg font-medium pr-8">{question}</span>
        <ChevronDown size={20} className={`shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-500 ${isOpen ? 'max-h-[800px] pb-6' : 'max-h-0'}`}>
        <p className="text-white/70 leading-relaxed font-medium">{answer}</p>
        {renderExtendedContent()}
      </div>
    </div>
  );
};

// Module Card Component
const ModuleCard = ({ module, index }) => (
  <div className="bg-white/[0.03] border border-white/10 rounded-xl p-6 hover:border-[#E5C77A]/30 transition-all duration-300">
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#C6A85E]/20 to-[#E5C77A]/10 flex items-center justify-center shrink-0">
        <span className="text-[#E5C77A] font-semibold">{String(index + 1).padStart(2, '0')}</span>
      </div>
      <div>
        <h4 className="font-semibold text-white mb-1">{module.title}</h4>
        <p className="text-sm text-[#E5C77A]/80 mb-2">{module.duration}</p>
        <p className="text-sm text-white/60">{module.description}</p>
      </div>
    </div>
  </div>
);

const AIToolsForCreatives = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Course Schema for SEO
  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "AI for Creatives: Build IP That Sells",
    "description": "Learn the complete AI workflow to take your story from concept to pitch-ready. 11 modules, templates & community access.",
    "provider": {
      "@type": "Person",
      "name": "Ramon Govea",
      "sameAs": "https://ramongovea.com"
    },
    "instructor": {
      "@type": "Person",
      "name": "Ramon Govea",
      "description": "Hollywood veteran with 15+ years experience. Worked with Stan Lee, Baron Davis. Sold 10 IPs, raised $2.6M."
    },
    "offers": [
      {
        "@type": "Offer",
        "name": "Foundation",
        "price": "147",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock"
      },
      {
        "@type": "Offer",
        "name": "Pro",
        "price": "497",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock"
      },
      {
        "@type": "Offer",
        "name": "VIP",
        "price": "1997",
        "priceCurrency": "USD",
        "availability": "https://schema.org/LimitedAvailability"
      }
    ],
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "online",
      "courseWorkload": "PT2H30M"
    }
  };

  return (
    <div className="min-h-screen bg-[#08090d] text-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{SEO.title}</title>
        <meta name="description" content={SEO.description} />
        <link rel="canonical" href={SEO.url} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="AI for Creatives: Build IP That Sells" />
        <meta property="og:description" content="From concept to pitch-ready: The complete AI system for solo creators. 11 modules, templates, lifetime access." />
        <meta property="og:image" content={SEO.ogImage} />
        <meta property="og:url" content={SEO.url} />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI for Creatives: Build IP That Sells" />
        <meta name="twitter:description" content="The complete AI workflow for solo creators. 11 modules, templates & lifetime access." />
        <meta name="twitter:image" content={SEO.ogImage} />
        
        {/* Course Schema */}
        <script type="application/ld+json">
          {JSON.stringify(courseSchema)}
        </script>
      </Helmet>

      {/* CSS */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .animate-fade-in { animation: fadeInUp 0.8s ease-out forwards; }
        .animate-fade-in-delay-1 { animation: fadeInUp 0.8s ease-out 0.1s forwards; opacity: 0; }
        .animate-fade-in-delay-2 { animation: fadeInUp 0.8s ease-out 0.2s forwards; opacity: 0; }
        .animate-fade-in-delay-3 { animation: fadeInUp 0.8s ease-out 0.3s forwards; opacity: 0; }
        .serif-heading { font-family: 'Playfair Display', Georgia, serif; }
        .premium-button {
          background: linear-gradient(135deg, #C6A85E, #E5C77A);
          box-shadow: 0 8px 32px rgba(198, 168, 94, 0.3);
          transition: all 250ms ease;
        }
        .premium-button:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 40px rgba(198, 168, 94, 0.4);
        }
        .secondary-button {
          background: transparent;
          border: 2px solid rgba(198, 168, 94, 0.5);
          transition: all 250ms ease;
        }
        .secondary-button:hover {
          border-color: #E5C77A;
          background: rgba(198, 168, 94, 0.1);
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(12px);
        }
        .pro-tier-card {
          background: linear-gradient(180deg, rgba(198, 168, 94, 0.08) 0%, rgba(198, 168, 94, 0.02) 100%);
          border: 2px solid rgba(198, 168, 94, 0.4);
        }
        .gradient-text {
          background: linear-gradient(135deg, #E5C77A 0%, #C6A85E 50%, #E5C77A 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .credential-badge {
          background: rgba(229, 199, 122, 0.08);
          border: 1px solid rgba(229, 199, 122, 0.2);
        }
      `}</style>

      {/* Sticky CTA Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#08090d]/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <span className="text-sm text-white/70 hidden md:block">AI Tools for Creatives — Launch Price: <span className="text-[#E5C77A] font-semibold">$147</span></span>
          <button 
            onClick={() => scrollToSection('pricing')}
            className="premium-button text-[#0F1115] px-6 py-2 text-sm font-semibold tracking-wide rounded-md"
          >
            Get Started
          </button>
        </div>
      </div>

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F1218] via-[#08090d] to-[#08090d]" />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#C6A85E]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-[#3C6DF0]/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h1 className="serif-heading text-[38px] md:text-[48px] lg:text-[56px] font-normal leading-[1.15] mb-6 animate-fade-in">
            Ship Pitch-Ready IP in Half the Time — <span className="gradient-text">Without Hiring a $100K Team</span> or Selling Your Soul to AI
          </h1>
          
          <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto mb-8 leading-relaxed animate-fade-in-delay-1">
            From a creator who raised $2.6M:<br />
            Learn the complete system to take your story from concept to pitch-ready—without needing a Hollywood budget, while keeping <strong className="text-white">YOU</strong> at the center of every decision.
          </p>

          {/* Credentials Bar */}
          <div className="flex flex-wrap justify-center gap-3 mb-10 animate-fade-in-delay-2">
            {[
              "Worked with Stan Lee & Baron Davis",
              "10 IPs sold including Lion Forge deal",
              "$1.6M raised on Kickstarter",
              "$1M+ in digital collectibles"
            ].map((cred, i) => (
              <span key={i} className="credential-badge px-4 py-2 rounded-full text-sm text-white/80">
                <Check size={14} className="inline mr-2 text-[#E5C77A]" />{cred}
              </span>
            ))}
          </div>

          {/* Primary CTA */}
          <div className="animate-fade-in-delay-3">
            <div className="mb-3">
              <span className="inline-block bg-[#E5C77A]/20 text-[#E5C77A] text-sm font-semibold px-4 py-1.5 rounded-full">
                $147 — Normally $297 (50% OFF)
              </span>
            </div>
            <button 
              onClick={() => scrollToSection('pricing')}
              className="premium-button text-[#0F1115] px-10 py-5 text-lg font-semibold tracking-wide rounded-lg inline-flex items-center gap-3"
              data-testid="hero-cta"
            >
              Start Building Now
              <ChevronRight size={20} />
            </button>
            
            <p className="text-sm text-white/50 mt-4">
              <span className="text-[#E5C77A]">Launch discount:</span> Price increases after first 50 students
            </p>
            
            <p className="text-sm text-white/40 mt-2">
              11 video modules • 10+ templates • Story Framework Formula book included
            </p>
          </div>
        </div>
      </section>

      {/* THE PROBLEM SECTION */}
      <section className="py-24 px-6 border-t border-white/[0.06]">
        <div className="max-w-3xl mx-auto">
          <h2 className="serif-heading text-[36px] md:text-[44px] font-normal text-center mb-12 md:whitespace-nowrap">
            You Have the Story. You Don't Have the Team.
          </h2>
          
          <div className="text-lg text-white/80 leading-relaxed space-y-6">
            <p>You're a writer, a world-builder, a creator with a vision that's crystal-clear in your head.</p>
            <p>But here's the problem:</p>
            
            <div className="space-y-3 py-4">
              {[
                "You can't draw (or can't afford the $5K-$15K an artist charges for character sheets + environments)",
                "You can't afford to hire animators or voice actors (and shouldn't have to just to prove your concept)",
                "You can't visualize your characters, environments, or key scenes",
                "You can't create a pitch deck that makes buyers take you seriously"
              ].map((problem, i) => (
                <div key={i} className="flex items-start gap-3">
                  <X size={20} className="text-red-400/80 mt-1 shrink-0" />
                  <span className="text-white/70">{problem}</span>
                </div>
              ))}
            </div>
            
            <p>So your story stays locked in your head. Or worse—trapped in a Word document nobody will ever see.</p>
            
            <p className="text-white/80 pt-2">Here's what changed: <strong className="text-white">AI tools crossed the threshold.</strong> You can now visualize your IP—without hiring a team—and keep full creative control.</p>
            
            <p className="text-[#E5C77A] text-xl font-semibold pt-4">Those days are over.</p>
          </div>
        </div>
      </section>

      {/* THE SOLUTION SECTION */}
      <section className="py-24 px-6 bg-white/[0.02]">
        <div className="max-w-5xl mx-auto">
          <h2 className="serif-heading text-[36px] md:text-[44px] font-normal text-center mb-6">
            AI Changed Everything. Here's How to Use It Without Selling Your Soul.
          </h2>
          <p className="text-center text-white/60 mb-16 max-w-2xl mx-auto">I'm Ramon Govea. Here's what I've done over 15 years:</p>
          
          {/* Credentials Grid */}
          <div className="grid md:grid-cols-2 gap-4 mb-16">
            {[
              "Worked with Stan Lee (Marvel creator) and Baron Davis (2x NBA All-Star) as his Chief Content Officer",
              "Sold a 20-issue comic series to Lion Forge Comics (distributed via Diamond Comics)",
              "Collaborated with Joe Casey (X-Men writer, Ben 10 co-creator)",
              "Co-wrote 10 comic books with Brandon Easton (Award-winning MAX showrunner of Iyanu)",
              "Worked on marketing for The Walking Dead: Road to Survival (Scopely)",
              "Worked for Nolan Bushnell (Atari & Chuck E. Cheese founder)",
              "Self-published 30 comic books",
              "Sold or licensed 10 different IPs",
              "Raised $1.6M via Kickstarter/Indiegogo",
              "Sold $1M+ in digital collectibles"
            ].map((cred, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-lg bg-white/[0.02]">
                <Check size={18} className="text-[#E5C77A] mt-1 shrink-0" />
                <span className="text-white/80 text-sm">{cred}</span>
              </div>
            ))}
          </div>

          <div className="text-center mb-12">
            <p className="text-xl text-white/90 mb-4"><strong>I know what Hollywood-level creative looks like.</strong></p>
            <p className="text-lg text-white/70 mb-6">I also know most creators can't afford it.</p>
            <p className="text-lg text-white/70">So I reverse-engineered the entire process using AI tools—not to replace artists, but to <strong className="text-white">empower solo creators</strong> who can't hire a giant team.</p>
          </div>

          {/* Ethical Framework Callout */}
          <div className="mb-16 max-w-2xl mx-auto">
            <div className="relative rounded-xl p-8 bg-gradient-to-br from-[#1a2035] to-[#0f1420] border-l-4 border-[#3C6DF0]">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#3C6DF0]/5 rounded-full blur-2xl" />
              <h3 className="text-xl font-semibold text-white mb-5 flex items-center gap-3">
                <Shield size={22} className="text-[#3C6DF0]" />
                The Ethical Framework
              </h3>
              <ul className="space-y-4 text-white/80">
                <li className="flex items-start gap-3">
                  <Check size={18} className="text-[#3C6DF0] mt-1 shrink-0" />
                  <span><strong className="text-white">AI for R&D and iteration</strong>—visual development, not final assets</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check size={18} className="text-[#3C6DF0] mt-1 shrink-0" />
                  <span><strong className="text-white">When you can afford to hire artists, you will</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <Check size={18} className="text-[#3C6DF0] mt-1 shrink-0" />
                  <span><strong className="text-white">You shouldn't have to wait years</strong> just to prove your concept deserves funding</span>
                </li>
              </ul>
            </div>
          </div>

          {/* 3 Columns */}
          <div className="grid md:grid-cols-3 gap-8">
            <div className="glass-card rounded-xl p-8">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#C6A85E]/20 to-transparent flex items-center justify-center mb-6">
                <Sparkles size={24} className="text-[#E5C77A]" />
              </div>
              <h3 className="text-xl font-semibold mb-4">For Creatives</h3>
              <ul className="space-y-3 text-white/70 text-sm">
                <li className="flex items-center gap-2"><Check size={16} className="text-[#E5C77A]" />Character design with consistency</li>
                <li className="flex items-center gap-2"><Check size={16} className="text-[#E5C77A]" />Environment & world visualization</li>
                <li className="flex items-center gap-2"><Check size={16} className="text-[#E5C77A]" />Storyboarding & scene layout</li>
                <li className="flex items-center gap-2"><Check size={16} className="text-[#E5C77A]" />AI voice & video tools</li>
              </ul>
            </div>

            <div className="glass-card rounded-xl p-8">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#C6A85E]/20 to-transparent flex items-center justify-center mb-6">
                <BookOpen size={24} className="text-[#E5C77A]" />
              </div>
              <h3 className="text-xl font-semibold mb-4">For Storytellers</h3>
              <ul className="space-y-3 text-white/70 text-sm">
                <li className="flex items-center gap-2"><Check size={16} className="text-[#E5C77A]" />Story bible frameworks</li>
                <li className="flex items-center gap-2"><Check size={16} className="text-[#E5C77A]" />World-building techniques</li>
                <li className="flex items-center gap-2"><Check size={16} className="text-[#E5C77A]" />Writing with AI as co-pilot</li>
                <li className="flex items-center gap-2"><Check size={16} className="text-[#E5C77A]" />Ethics & best practices</li>
              </ul>
            </div>

            <div className="glass-card rounded-xl p-8">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#C6A85E]/20 to-transparent flex items-center justify-center mb-6">
                <Target size={24} className="text-[#E5C77A]" />
              </div>
              <h3 className="text-xl font-semibold mb-4">For Selling IP</h3>
              <ul className="space-y-3 text-white/70 text-sm">
                <li className="flex items-center gap-2"><Check size={16} className="text-[#E5C77A]" />Pitch deck creation</li>
                <li className="flex items-center gap-2"><Check size={16} className="text-[#E5C77A]" />What buyers actually want</li>
                <li className="flex items-center gap-2"><Check size={16} className="text-[#E5C77A]" />Complete production workflow</li>
                <li className="flex items-center gap-2"><Check size={16} className="text-[#E5C77A]" />Real IP sales lessons</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* COURSE BREAKDOWN SECTION */}
      <section className="py-24 px-6 border-t border-white/[0.06]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="serif-heading text-[36px] md:text-[44px] font-normal mb-8">
            What You'll Walk Away With
          </h2>
          
          <div className="space-y-6 text-lg text-white/80 leading-relaxed mb-10">
            <p>
              Most creators never crack the Hollywood development code — not because they lack talent, but because no one ever showed them how the machine actually works.
            </p>
            <p>
              In 11 focused modules, you'll get the insider knowledge that working producers charge $5K-$25K to share privately — combined with AI workflows that compress years of trial and error into days.
            </p>
            <p>
              You'll know how to visualize your world, develop your characters, build your pitch deck, and present your IP the way Hollywood actually expects to see it. The knowledge compacted into this course garnered a <strong className="text-white">seven figure valuation offer</strong> for the instructor's slate of original IP.
            </p>
            <p className="text-[#E5C77A] font-medium">
              AI handles the heavy lifting. You stay in the director's chair.
            </p>
          </div>
          
          <p className="text-white/50 text-sm tracking-wide uppercase">
            Everything you need. Nothing you don't.
          </p>
        </div>
      </section>

      {/* WHO THIS IS FOR SECTION */}
      <section className="py-24 px-6 bg-white/[0.02]">
        <div className="max-w-3xl mx-auto">
          <h2 className="serif-heading text-[36px] md:text-[44px] font-normal text-center mb-12">
            This Course Is For You If...
          </h2>
          
          <div className="space-y-4">
            {[
              { text: "You're a writer with a story but no way to visualize it", highlight: "writer" },
              { text: "You're an artist who wants more creative control of your original ideas", highlight: "artist" },
              { text: "You're an indie creator who can't afford to hire a team", highlight: "indie creator" },
              { text: "You're a world-builder who wants to see your universe come to life", highlight: "world-builder" },
              { text: "You're a screenwriter who needs a visual pitch deck", highlight: "screenwriter" },
              { text: "You're a creative entrepreneur who wants to build IP that sells", highlight: "entrepreneur" },
              { text: "You're intimidated by AI but know you need to learn it", highlight: "intimidated by AI" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4 p-4 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                <div className="w-8 h-8 rounded-full bg-[#E5C77A]/20 flex items-center justify-center shrink-0">
                  <Check size={18} className="text-[#E5C77A]" />
                </div>
                <span className="text-white/80">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSTRUCTOR SECTION */}
      <section className="py-24 px-6 border-t border-white/[0.06]">
        <div className="max-w-5xl mx-auto">
          <h2 className="serif-heading text-[36px] md:text-[44px] font-normal text-center mb-16">
            Meet Your Instructor: American Ape
          </h2>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_6ff09686-3a90-4ae2-8c29-5f23b98b61ac/artifacts/07xyxzhp_American%20ApeTeacher.PNG"
                alt="American Ape - Your AI Creative Coach"
                className="rounded-2xl w-full shadow-2xl shadow-black/50"
              />
              <div className="absolute -bottom-4 -right-4 bg-[#08090d] border border-white/10 rounded-xl px-4 py-2">
                <span className="text-sm text-white/60">10 comic issues in production</span>
              </div>
            </div>
            
            <div className="space-y-6 text-white/80 leading-relaxed">
              <p>
                American Ape is a genetically enhanced, hyper-intelligent chimp I created that's got 10 comic book issues produced and an animated series in development. It's part of an IP that was successfully licensed and part of a slate that a studio offered 7 figures to acquire. But today, he's your teacher.
              </p>
              
              <p className="text-[#E5C77A] italic">Why a chimp?</p>
              
              <p>Because traditional courses are BORING. This one isn't.</p>
              
              <div className="space-y-3 py-4">
                {[
                  { title: "Experimental teacher", desc: "patient, clear, no fluff" },
                  { title: "Straight talk", desc: "acknowledges AI's limits honestly" },
                  { title: "Real tactics", desc: "based on Ramon's 15 years selling IP in Hollywood" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Zap size={18} className="text-[#E5C77A] mt-1 shrink-0" />
                    <span><strong className="text-white">{item.title}</strong> — {item.desc}</span>
                  </div>
                ))}
              </div>
              
              <div className="p-4 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                <p className="text-sm text-white/60">
                  <span className="text-white/80">Behind the character:</span> This course was created by <strong className="text-white">Ramon Govea</strong>, who wrote every lesson based on real experience selling & licensing IP to multiple studios, collaborating with Marvel & DC writers, producing for Stan Lee, and selling $2.6M direct-to-consumer.
                </p>
              </div>
              
              <p className="text-lg font-semibold text-white">American Ape is the teacher. Ramon provides the experience.</p>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED SECTION */}
      <section className="py-24 px-6 bg-white/[0.02]">
        <div className="max-w-3xl mx-auto">
          <h2 className="serif-heading text-[36px] md:text-[44px] font-normal text-center mb-16">
            Everything You Get
          </h2>
          
          {/* Foundation Tier */}
          <div className="mb-10">
            <h3 className="text-xl font-semibold text-[#E5C77A] mb-6">Foundation ($147):</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Check size={20} className="text-[#E5C77A] mt-1 shrink-0" />
                <div>
                  <span className="text-white">11 Video Modules</span>
                  <span className="text-white/70"> — insider Hollywood development knowledge that took 15 years and 10 sold IPs to accumulate (private consulting for this runs $5K-$25K per project)</span>
                  <span className="text-[#E5C77A] ml-2">— $5,000 value</span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Check size={20} className="text-[#E5C77A] mt-1 shrink-0" />
                <div>
                  <span className="text-white">Story Framework Formula</span>
                  <span className="text-white/70"> (120+ page development book)</span>
                  <span className="text-[#E5C77A] ml-2">— $47 value</span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Check size={20} className="text-[#E5C77A] mt-1 shrink-0" />
                <div>
                  <span className="text-white">Professional Templates + IP Development Prompt Packs</span>
                  <span className="text-[#E5C77A] ml-2">— $497 value</span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Check size={20} className="text-[#E5C77A] mt-1 shrink-0" />
                <div>
                  <span className="text-white">Private Skool Community</span>
                  <span className="text-white/70"> (lifetime access + Ramon's direct answers)</span>
                  <span className="text-[#E5C77A] ml-2">— $97 value</span>
                </div>
              </div>
            </div>
            
            {/* Value Stack Summary */}
            <div className="mt-8 p-6 rounded-xl bg-white/[0.03] border border-white/[0.08]">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <p className="text-white/60 text-sm">Total Value:</p>
                  <p className="text-2xl font-bold text-white">$5,641</p>
                </div>
                <div className="text-left md:text-right">
                  <p className="text-white/60 text-sm">Your Price Today:</p>
                  <p className="text-2xl font-bold text-[#E5C77A]">$147 <span className="text-sm font-normal text-white/50">(97% off)</span></p>
                </div>
              </div>
            </div>
          </div>

          {/* Pro Tier Addition */}
          <div className="mb-8 p-6 rounded-xl bg-gradient-to-r from-[#C6A85E]/10 to-transparent border border-[#C6A85E]/30">
            <h3 className="text-lg font-semibold text-white mb-4">Pro ($497) also includes:</h3>
            <div className="flex items-start gap-3">
              <Check size={20} className="text-[#E5C77A] mt-1 shrink-0" />
              <div>
                <span className="text-white">Twice-Monthly 45-Min Group Zoom Q&A with Ramon</span>
                <span className="text-[#E5C77A] ml-2">— $750/month value</span>
              </div>
            </div>
          </div>

          {/* VIP Tier Addition */}
          <div className="mb-10 p-6 rounded-xl bg-gradient-to-r from-[#C6A85E]/10 to-transparent border border-[#C6A85E]/30">
            <h3 className="text-lg font-semibold text-white mb-4">VIP ($1,997) also includes:</h3>
            <div className="flex items-start gap-3">
              <Check size={20} className="text-[#E5C77A] mt-1 shrink-0" />
              <div>
                <span className="text-white">90-Min 1-on-1 Pitch Review with Ramon</span>
                <span className="text-[#E5C77A] ml-2">— $750 value</span>
              </div>
            </div>
          </div>

          {/* Guarantee Footer */}
          <p className="text-center text-white/60 text-sm">
            One-time payment. Lifetime access. 30-day money-back guarantee.
          </p>
        </div>
      </section>

      {/* PRICING SECTION */}
      <section id="pricing" className="py-24 px-6 border-t border-white/[0.06]">
        <div className="max-w-6xl mx-auto">
          <h2 className="serif-heading text-[36px] md:text-[44px] font-normal text-center mb-4">
            Choose Your Path
          </h2>
          <p className="text-center text-white/60 mb-16">Select the tier that matches your goals</p>
          
          <div className="grid lg:grid-cols-3 gap-6">
            {/* TIER 1: FOUNDATION */}
            <div className="glass-card rounded-2xl p-8 flex flex-col">
              <div className="mb-6">
                <h3 className="text-xl font-semibold mb-2">Foundation</h3>
                <p className="text-white/60 text-sm">Perfect for solo creators who want to build</p>
              </div>
              
              <div className="mb-6">
                <span className="text-4xl font-bold">$147</span>
                <span className="text-white/50 ml-2 line-through">$297</span>
              </div>
              
              <ul className="space-y-3 mb-8 flex-grow">
                {[
                  "All 11 video modules (delivered weekly)",
                  "Story Framework Formula book",
                  "All 10+ templates & prompts",
                  "Private community access",
                  "Lifetime access to course"
                ].map((feature, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-white/70">
                    <Check size={16} className="text-[#E5C77A] mt-0.5 shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              
              <p className="text-xs text-white/50 mb-4">Best for writers, storytellers, and creators who want to visualize their IP.</p>
              
              {/* ROI Breakdown */}
              <div className="mb-6 p-4 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                <p className="text-xs text-white/70 leading-relaxed">
                  <strong className="text-white/90">ROI breakdown:</strong> Hiring a character designer alone costs $2K-$5K. This course teaches you to do R&D for all your characters, environments, and pitch materials for $147. After your first funded project, this pays for itself many times over.
                </p>
              </div>
              
              <button 
                onClick={() => window.location.href = '/ai-tools-for-creatives/checkout/foundation'}
                className="secondary-button text-[#E5C77A] w-full py-4 rounded-lg font-semibold"
                data-testid="tier-foundation-btn"
              >
                Claim Your Spot — $147
              </button>
            </div>

            {/* TIER 2: PRO - MOST POPULAR */}
            <div className="pro-tier-card rounded-2xl p-8 flex flex-col relative lg:scale-105 lg:-my-4">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-[#E5C77A] text-[#0F1115] px-4 py-1 rounded-full text-xs font-bold tracking-wider">
                  MOST POPULAR
                </span>
              </div>
              
              <div className="mb-6 pt-2">
                <h3 className="text-xl font-semibold mb-2">Pro</h3>
                <p className="text-white/60 text-sm">For creators who want to sell their IP</p>
              </div>
              
              <div className="mb-6">
                <span className="text-4xl font-bold text-[#E5C77A]">$497</span>
              </div>
              
              <p className="text-sm text-white/60 mb-4">Everything in Foundation, PLUS:</p>
              
              <ul className="space-y-3 mb-8 flex-grow">
                {[
                  "\"How I Sold 10 IPs\" Bonus Module (4 lessons)",
                  "Pitch Deck Review (Ramon's feedback on YOUR deck)",
                  "Crowdfunding Campaign Checklist ($1.6M Kickstarter)",
                  "The Lion Forge Deal breakdown",
                  "What buyers actually want to see"
                ].map((feature, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-white/80">
                    <Check size={16} className="text-[#E5C77A] mt-0.5 shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              
              <p className="text-xs text-white/50 mb-4">Best for creators ready to pitch to publishers, studios, or investors.</p>
              
              <button 
                onClick={() => window.location.href = '/ai-tools-for-creatives/checkout/pro'}
                className="premium-button text-[#0F1115] w-full py-4 rounded-lg font-semibold"
                data-testid="tier-pro-btn"
              >
                Get Pro Access — $497
              </button>
            </div>

            {/* TIER 3: VIP */}
            <div className="glass-card rounded-2xl p-8 flex flex-col">
              <div className="mb-6">
                <h3 className="text-xl font-semibold mb-2">VIP</h3>
                <p className="text-white/60 text-sm">For serious creators who want direct access</p>
              </div>
              
              <div className="mb-6">
                <span className="text-4xl font-bold">$1,997</span>
              </div>
              
              <p className="text-sm text-white/60 mb-4">Everything in Pro, PLUS:</p>
              
              <ul className="space-y-3 mb-8 flex-grow">
                {[
                  "90-Minute 1-on-1 Strategy Call with Ramon",
                  "Pitch Practice Session (real-time coaching)",
                  "Industry Pitch Feedback (when applicable)",
                  "Lifetime Priority Support",
                  "First in line for future updates"
                ].map((feature, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-white/70">
                    <Check size={16} className="text-[#E5C77A] mt-0.5 shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              
              <p className="text-xs text-[#E5C77A] mb-4">Only 10 spots available</p>
              
              <button 
                onClick={() => window.location.href = '/ai-tools-for-creatives/checkout/vip'}
                className="secondary-button text-[#E5C77A] w-full py-4 rounded-lg font-semibold"
                data-testid="tier-vip-btn"
              >
                Get VIP Access — $1,997
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF SECTION */}
      <section className="py-24 px-6 bg-white/[0.02]">
        <div className="max-w-5xl mx-auto">
          <h2 className="serif-heading text-[36px] md:text-[44px] font-normal text-center mb-16">
            Proven Track Record
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="glass-card rounded-xl p-6">
              <h4 className="text-[#E5C77A] font-semibold mb-4">Publishing</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li>20-issue series to Lion Forge Comics</li>
                <li>Diamond Comics distribution</li>
                <li>Premium Series for Tapas Media</li>
                <li>30 self-published comic books</li>
              </ul>
            </div>
            
            <div className="glass-card rounded-xl p-6">
              <h4 className="text-[#E5C77A] font-semibold mb-4">Collaborations</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li>Stan Lee (Marvel creator)</li>
                <li>Joe Casey (X-Men, Ben 10)</li>
                <li>Brandon Easton (HBO showrunner)</li>
                <li>Joe Illidge (Batman: NML Editor)</li>
                <li>Jon Lam (Sr. Artist Riot Games)</li>
              </ul>
            </div>
            
            <div className="glass-card rounded-xl p-6">
              <h4 className="text-[#E5C77A] font-semibold mb-4">Revenue</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li>$1.6M Kickstarter/Indiegogo</li>
                <li>$1M+ digital collectibles</li>
                <li>10 IPs sold or licensed</li>
                <li>2 Screenplays optioned</li>
              </ul>
            </div>
            
            <div className="glass-card rounded-xl p-6">
              <h4 className="text-[#E5C77A] font-semibold mb-4">Hollywood</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li>Baron Davis (CCO, 3 years)</li>
                <li>The Walking Dead (Scopely)</li>
                <li>Series sold to Warner Bros</li>
                <li>Nolan Bushnell (Atari & Chuck E. Cheese founder)</li>
              </ul>
            </div>
          </div>
          
          <p className="text-center text-white/50 italic mt-12">
            "Ramon doesn't just teach IP creation. He's SOLD IP. That's the difference."
          </p>
        </div>
      </section>

      {/* SCARCITY SECTION */}
      <section className="py-24 px-6 border-t border-white/[0.06]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="serif-heading text-[36px] md:text-[44px] font-normal mb-4">
              Why Launch Pricing Won't Last
            </h2>
            <p className="text-lg text-white/60 italic">This is not a fake deadline.</p>
          </div>
          
          <div className="space-y-8">
            <p className="text-lg text-white/80 text-center">
              Here's what happens after the first 50 students enroll:
            </p>
            
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-5 rounded-xl bg-white/[0.03] border border-white/[0.08]">
                <div className="w-8 h-8 rounded-full bg-[#E5C77A]/20 flex items-center justify-center shrink-0">
                  <span className="text-[#E5C77A] font-bold">1</span>
                </div>
                <p className="text-white/80">
                  <strong className="text-white">Price increases to $297</strong> (permanent)
                </p>
              </div>
              
              <div className="flex items-start gap-4 p-5 rounded-xl bg-white/[0.03] border border-white/[0.08]">
                <div className="w-8 h-8 rounded-full bg-[#E5C77A]/20 flex items-center justify-center shrink-0">
                  <span className="text-[#E5C77A] font-bold">2</span>
                </div>
                <p className="text-white/80">
                  <strong className="text-white">Live Q&A Access</strong> moves to Pro & VIP tiers only
                </p>
              </div>
              
              <div className="flex items-start gap-4 p-5 rounded-xl bg-white/[0.03] border border-white/[0.08]">
                <div className="w-8 h-8 rounded-full bg-[#E5C77A]/20 flex items-center justify-center shrink-0">
                  <span className="text-[#E5C77A] font-bold">3</span>
                </div>
                <p className="text-white/80">
                  <strong className="text-white">VIP 1-on-1 review slots are capped</strong> — once they're gone, they're gone
                </p>
              </div>
            </div>
            
            <div className="pt-8 space-y-6 text-center">
              <p className="text-white/70 leading-relaxed">
                Once we hit 50, launch pricing is gone. No extensions. No exceptions. Why the limit? Ramon personally does VIP 1-on-1 strategy calls. That time doesn't scale — so founding member enrollment is capped.
              </p>
              <p className="text-lg text-[#E5C77A] font-medium">
                If you're reading this and the price is still $147, you're early. Lock it in now.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 px-6 border-t border-white/[0.06]">
        <div className="max-w-3xl mx-auto">
          <h2 className="serif-heading text-[36px] md:text-[44px] font-normal text-center mb-16">
            Frequently Asked Questions
          </h2>
          
          <div className="divide-y divide-white/10">
            {faqData.map((faq, i) => (
              <FAQItem key={i} question={faq.q} answer={faq.a} extended={faq.extended} />
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section className="py-24 px-6 bg-gradient-to-b from-white/[0.02] to-[#08090d]">
        <div className="max-w-4xl mx-auto">
          <h2 className="serif-heading text-[36px] md:text-[48px] font-normal text-center mb-10">
            Stop Waiting for Permission.<br />Your Story Deserves to Be Seen.
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-6 text-lg text-white/80 leading-relaxed mb-12 text-center">
            <p>
              For too long, great ideas have died in Word documents because creators couldn't afford a $50K-$100K team to visualize them — or didn't know how Hollywood actually works.
            </p>
            <p className="text-[#E5C77A] font-medium">Not anymore.</p>
            <p>
              AI tools have leveled the playing field. And now the insider knowledge that used to cost $5K-$25K in private consulting is available for $147.
            </p>
            <p>
              Character designs, environments, storyboards, pitch decks — in days, not years. For $147, not $50K.
            </p>
            <p className="text-white font-semibold">
              The tools are here. The knowledge is here. Your story is next.
            </p>
          </div>

          {/* What You'll Be Able to Do */}
          <div className="max-w-2xl mx-auto mb-12">
            <h3 className="text-xl font-semibold text-white text-center mb-6">What You'll Be Able to Do After This Course:</h3>
            <div className="space-y-3">
              {[
                "Pitch your IP to a buyer with a deck that looks like a studio made it",
                "Visualize characters, environments, and key scenes without hiring a single artist",
                "Validate your concept before burning runway on production",
                "Know exactly what Hollywood buyers need to see — and give it to them"
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Check size={20} className="text-[#E5C77A] mt-1 shrink-0" />
                  <span className="text-white/80">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Foundation Value Stack */}
          <div className="max-w-2xl mx-auto mb-10 p-8 rounded-xl bg-white/[0.03] border border-white/[0.08]">
            <h3 className="text-lg font-semibold text-[#E5C77A] mb-6">Foundation Value Stack:</h3>
            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3">
                <Check size={18} className="text-[#E5C77A] mt-1 shrink-0" />
                <span className="text-white/80 text-sm">11 Video Modules (insider Hollywood development knowledge, $5K-$25K in private consulting) — <span className="text-[#E5C77A]">$5,000 value</span></span>
              </div>
              <div className="flex items-start gap-3">
                <Check size={18} className="text-[#E5C77A] mt-1 shrink-0" />
                <span className="text-white/80 text-sm">Story Framework Formula — <span className="text-[#E5C77A]">$47 value</span></span>
              </div>
              <div className="flex items-start gap-3">
                <Check size={18} className="text-[#E5C77A] mt-1 shrink-0" />
                <span className="text-white/80 text-sm">Professional Templates + IP Development Prompt Packs — <span className="text-[#E5C77A]">$497 value</span></span>
              </div>
              <div className="flex items-start gap-3">
                <Check size={18} className="text-[#E5C77A] mt-1 shrink-0" />
                <span className="text-white/80 text-sm">Private Skool Community — <span className="text-[#E5C77A]">$97 value</span></span>
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-4 border-t border-white/10">
              <div>
                <p className="text-white/50 text-sm">Total Value:</p>
                <p className="text-2xl font-bold text-white">$5,641</p>
              </div>
              <div className="md:text-right">
                <p className="text-white/50 text-sm">Foundation Price:</p>
                <p className="text-2xl font-bold text-[#E5C77A]">$147 <span className="text-sm font-normal text-white/50">(97% off)</span></p>
              </div>
            </div>
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <button 
              onClick={() => window.location.href = '/ai-tools-for-creatives/checkout/foundation'}
              className="premium-button text-[#0F1115] px-10 py-5 text-lg font-semibold tracking-wide rounded-lg"
            >
              Start Building Now — $147
            </button>
            <button 
              onClick={() => scrollToSection('pricing')}
              className="secondary-button text-[#E5C77A] px-10 py-5 text-lg font-semibold tracking-wide rounded-lg"
            >
              View All Tiers (Pro $497 | VIP $1,997)
            </button>
          </div>
          
          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 text-sm text-white/50">
            {[
              "30-Day Money-Back Guarantee",
              "Lifetime Access",
              "Private Community",
              "One-time payment. No subscriptions."
            ].map((badge, i) => (
              <span key={i} className="flex items-center gap-2">
                <Check size={14} className="text-[#E5C77A]" />
                {badge}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 px-6 border-t border-white/[0.06]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-white/60 mb-4">Questions?</p>
          <a href="mailto:support@ramongovea.com" className="text-[#E5C77A] hover:underline flex items-center justify-center gap-2 mb-8">
            <Mail size={18} />
            support@ramongovea.com
          </a>
          
          <div className="text-white/40 text-sm">
            <p className="font-semibold text-white/60 mb-2">Created by Ramon Govea</p>
            <p>Hollywood veteran • 10 IPs sold • $2.6M raised • Your AI creative coach</p>
          </div>
          
          <p className="text-xs text-white/30 mt-8">
            © 2026 Ramon Govea. All rights reserved. · <a href="/privacy-policy" className="hover:text-white/50 underline">Privacy Policy</a> · <a href="/terms-of-service" className="hover:text-white/50 underline">Terms of Service</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AIToolsForCreatives;
